/** @jsx jsx */
import { memo, useCallback, useState } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { setIsViewConfigModifiedRecord } from '@jira/issue-navigator__saved-views-utils/src/setIsViewConfigModifiedRecord.tsx';
import Button, { IconButton } from '@atlaskit/button/new';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import UndoIcon from '@atlaskit/icon/core/undo';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useFlagsService } from '@atlassian/jira-flags';
import type { SaveResetViewSettingsButtonUI$key } from './__generated__/SaveResetViewSettingsButtonUI.graphql';
import type { SaveResetViewSettingsButtonUIDiscardSettingsMutation } from './__generated__/SaveResetViewSettingsButtonUIDiscardSettingsMutation.graphql';
import type { SaveResetViewSettingsButtonUIPublishSettingsMutation } from './__generated__/SaveResetViewSettingsButtonUIPublishSettingsMutation.graphql';
import messages from './messages.tsx';

const tooltipOptions = {
	hideTooltipOnClick: true,
} as const;

type Props = {
	onReset: () => void;
	viewFragment: SaveResetViewSettingsButtonUI$key;
	jql: string;
};

export const SaveResetViewSettingsButtonUI = memo(({ onReset, viewFragment, jql }: Props) => {
	const { formatMessage } = useIntl();
	const [isModalOpen, setModalOpen] = useState(false);
	const openModal = useCallback(() => setModalOpen(true), []);
	const closeModal = useCallback(() => setModalOpen(false), []);
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const viewMetadata = useFragment(
		graphql`
			fragment SaveResetViewSettingsButtonUI on JiraIssueSearchViewMetadata {
				__typename
				__id
				canPublishViewConfig
				savedViewId
				... on JiraSpreadsheetView {
					__typename
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						groupByConfig {
							groupByField {
								fieldId
							}
						}
						hideDone
						isHierarchyEnabled
					}
				}
			}
		`,
		viewFragment,
	);

	const canPublishViewConfig = viewMetadata.canPublishViewConfig;
	const savedViewId = viewMetadata.savedViewId;

	if (savedViewId == null) {
		throw new Error('Missing saved view ID');
	}

	const [publish, publishing] = useMutation<SaveResetViewSettingsButtonUIPublishSettingsMutation>(
		graphql`
			mutation SaveResetViewSettingsButtonUIPublishSettingsMutation(
				$groupBy: String
				$input: JiraPublishIssueSearchConfigInput!
				$issueSearchInput: JiraIssueSearchInput
				$settings: JiraIssueSearchSettings
				$staticViewInput: JiraIssueSearchStaticViewInput
			) {
				jira_publishIssueSearchConfig(input: $input) {
					success
					view {
						...SaveResetViewSettingsButtonCollapsible
						... on JiraIssueSearchViewMetadata {
							isViewConfigModified(settings: $settings)
						}
					}
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
	);

	const [discard, discarding] = useMutation<SaveResetViewSettingsButtonUIDiscardSettingsMutation>(
		graphql`
			mutation SaveResetViewSettingsButtonUIDiscardSettingsMutation(
				$input: JiraDiscardUserIssueSearchConfigInput!
			) {
				jira_discardUserIssueSearchConfig(input: $input) {
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
	);

	const onClickSave = useCallback(() => {
		const saveSettingsSuccessMessageGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.saveSettingsSuccessMessageGalaxia
			: messages.saveSettingsSuccessMessage;
		closeModal();

		const showFailureFlag = () => {
			showFlag({
				messageId:
					'issue-navigator-save-reset-view-settings-button.ui.show-flag.error.save-settings-failed',
				messageType: 'transactional',
				title: messages.saveSettingsFailedTitle,
				description: messages.saveSettingsFailedMessage,
				type: 'error',
			});
		};

		const viewSettings = viewMetadata.viewSettings;
		const settings = {
			groupBy: viewSettings?.groupByConfig?.groupByField?.fieldId ?? null,
			hideDone: viewSettings?.hideDone,
			hierarchyEnabled: viewSettings?.isHierarchyEnabled,
			jql,
		};

		publish({
			variables: {
				input: {
					viewId: savedViewId,
					settings,
				},
				settings,
			},
			/*
			 * This updater imperatively updates the isViewConfigModified record to handle the
			 * scenario where the variables provided to isViewConfigModified in the preloaded query
			 * differ from the variables provided in refetches.
			 *
			 * This ensures the save/reset view button is correctly hidden when publishing the view
			 * immediately after a page refresh.
			 *
			 * In all other scenarios we can rely on Relay writing the network response to the store
			 * via spreading a fragment which contains the isViewConfigModified field.
			 */
			updater: (store: RecordSourceSelectorProxy, response) => {
				const isViewConfigModified =
					response?.jira_publishIssueSearchConfig?.view?.isViewConfigModified;
				if (
					response?.jira_publishIssueSearchConfig?.success !== true ||
					isViewConfigModified == null
				) {
					return;
				}

				const viewRecord = store.get(viewMetadata.__id);
				if (viewRecord == null) {
					return;
				}

				setIsViewConfigModifiedRecord({
					isViewConfigModified,
					viewRecord,
				});
			},
			onError() {
				showFailureFlag();
			},
			onCompleted(response) {
				if (response.jira_publishIssueSearchConfig?.success !== true) {
					showFailureFlag();
					return;
				}

				showFlag({
					messageId:
						'issue-navigator-save-reset-view-settings-button.ui.show-flag.success.header.save-reset-view-settings.show-flag.success.save-settings-success',
					messageType: 'transactional',
					title: messages.saveSettingsSuccessTitle,
					description: saveSettingsSuccessMessageGalaxia,
					type: 'success',
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'viewSettings saved');
			},
		});

		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'saveSettings');
	}, [
		closeModal,
		createAnalyticsEvent,
		jql,
		publish,
		savedViewId,
		showFlag,
		viewMetadata.__id,
		viewMetadata.viewSettings,
	]);

	const onClickReset = useCallback(() => {
		const showFailureFlag = () => {
			showFlag({
				messageId:
					'issue-navigator-save-reset-view-settings-button.ui.show-flag.error.header.save-reset-view-settings.show-flag.error.reset-settings-failed',
				messageType: 'transactional',
				title: messages.resetSettingsFailedTitle,
				description: messages.resetSettingsFailedMessage,
				type: 'error',
			});
		};

		discard({
			variables: {
				input: {
					viewId: savedViewId,
				},
			},
			onError() {
				showFailureFlag();
			},
			onCompleted(response) {
				if (response.jira_discardUserIssueSearchConfig?.success !== true) {
					showFailureFlag();
					return;
				}

				onReset();
				fireTrackAnalytics(createAnalyticsEvent({}), 'viewSettings reset');
			},
		});

		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'resetSettings', {
			isProjectAdmin: canPublishViewConfig,
		});
	}, [canPublishViewConfig, createAnalyticsEvent, discard, onReset, savedViewId, showFlag]);

	if (!canPublishViewConfig) {
		return (
			<div css={styles.root}>
				<IconButton
					icon={UndoIcon}
					isLoading={discarding}
					isTooltipDisabled={false}
					label={formatMessage(messages.resetViewSettingsButton)}
					onClick={onClickReset}
					tooltip={tooltipOptions}
				/>
			</div>
		);
	}

	return (
		<div css={styles.root}>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						icon={SyncIcon}
						isLoading={discarding || publishing}
						isTooltipDisabled={false}
						label={formatMessage(messages.saveOrResetViewSettingsButton)}
						ref={triggerRef}
						tooltip={tooltipOptions}
					/>
				)}
				shouldRenderToParent={false}
				placement="bottom-end"
			>
				<DropdownItemGroup>
					<DropdownItem onClick={openModal}>
						{formatMessage(messages.saveViewSettingsMenuItem)}
					</DropdownItem>
					<DropdownItem onClick={onClickReset}>
						{formatMessage(messages.resetViewSettingsMenuItem)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			<ModalTransition>
				{isModalOpen && (
					<Modal onClose={closeModal} width="small">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.saveConfirmationModalTitle)}</ModalTitle>
						</ModalHeader>
						<ModalBody>{formatMessage(messages.saveConfirmationModalBody)}</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={closeModal}>
								{formatMessage(messages.cancelButton)}
							</Button>
							<Button appearance="primary" onClick={onClickSave}>
								{formatMessage(messages.saveButton)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</div>
	);
});

const styles = cssMap({
	root: {
		display: 'flex',
		// this allows to have the gap between action buttons being animated properly upon reveal/hide
		paddingLeft: token('space.100'),
		width: '32px',
	},
});
